<template>
  <div class="relative">
    <span
      id="datetime-picker-label"
      class="uppercase font-label text-fg-secondary text-sm inline-block"
    >
      <slot name="label">
        {{ label }}
      </slot>
    </span>
    <span
      id="datetime-picker-value-label"
      class="text-fg-link-primary block cursor-pointer"
      @click="open"
    >
      <slot name="datetimeLabel">
        {{ datetimeLabel || datetimeLabelFallback }}
      </slot>
    </span>
    <OnClickOutside
      id="datetime-picker-dropdown"
      v-show="isOpen"
      @trigger="close"
      class="!absolute left-0 top-7 bg-surface-primary rounded-md"
      :class="[modalShadowClass]"
    >
      <DatePicker
        id="date-picker"
        class="shadow-none"
        :selectedDate="selectedDate"
        :invalidDates="invalidDates"
        @datePickerChange="$emit('update:selectedDate', $event.date)"
      />
      <div class="px-6 pb-6">
        <TextInput
          id="time-picker"
          type="time"
          classes="w-3/5"
          :modelValue="selectedTime"
          @update:modelValue="$emit('update:selectedTime', $event)"
        />
      </div>
    </OnClickOutside>
  </div>
</template>

<script lang="ts">
import { OnClickOutside } from '@vueuse/components'
import { mapStores } from 'pinia'
import { PropType, defineComponent } from 'vue'

import TextInput from '@/common/components/molecules/TextInput.vue'
import DatePicker from '@/common/components/organisms/DatePicker.vue'
import { useSettingStore } from '@/stores/setting'

export default defineComponent({
  name: 'DateTimePicker',
  components: { TextInput, OnClickOutside, DatePicker },
  model: {
    prop: 'isOpen',
    event: 'update:isOpen',
  },
  props: {
    isOpen: {
      type: Boolean,
      required: true,
    },
    label: {
      type: String,
      required: true,
    },
    selectedDate: {
      type: String,
      required: true,
    },
    selectedTime: {
      type: String,
      required: true,
    },
    datetimeLabel: {
      type: String,
      required: false,
    },
    modalShadowClass: {
      type: String,
      required: false,
      default: 'shadow-podHover',
    },
    invalidDates: {
      type: [Array] as PropType<(string | 'before-today' | 'after-today')[]>,
      default: () => [] as string[],
    },
  },
  emits: {
    // eslint-disable-next-line no-unused-vars
    'update:selectedDate': (date: string) => true,
    // eslint-disable-next-line no-unused-vars
    'update:selectedTime': (time: string) => true,
    // eslint-disable-next-line no-unused-vars
    'update:isOpen': (isOpen: boolean) => true,
  },
  computed: {
    ...mapStores(useSettingStore),
    datetimeLabelFallback(): string {
      return this.settingStore.dateTimeStr(
        this.settingStore.makeDateTime({
          date: this.selectedDate,
          time: this.selectedTime,
        }),
      )
    },
  },
  methods: {
    async open() {
      if (this.isOpen) return
      await this.$emit('update:isOpen', true)
    },
    async close() {
      if (!this.isOpen) return
      await this.$emit('update:isOpen', false)
    },
  },
})
</script>
